import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Separator } from "./ui/separator";
import {
  FileText,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MessageSquare,
  Calendar,
  Search,
  Filter,
  PlusCircle,
  DollarSign,
  Users,
  BarChart3,
  Briefcase,
  Info,
  Upload,
  Paperclip,
  Download,
} from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { FileUploadSection } from "./FileUploadSection";
import { ADVISOR_FEE_TYPES, ANALYST_FEE_TYPES, RESEARCH_REQUEST_TYPES, ADVISING_REQUEST_TYPES, FINANCIAL_SPECIALIZATIONS } from "../utils/financialTaxonomy";
import { advisorService } from "@/services/advisorService";
import { researchAdvisingService } from "@/services/researchAdvisingService";
import { analystService } from "@/services/analystService";
import { uploadService } from "@/services/uploadService";
import { meetingService } from "@/services/meetingService";
import { useAuth } from "../hooks/useAuth";
import { ResearchDocument, ResearchAdvisingRequest } from "@/interface";
import { ServiceProvider } from "@/interface/ServiceProvider";
import { ScheduleMeetingDialog } from "./ScheduleMeetingDialog";
import { cn } from "./ui/utils";

interface ServiceRequest {
  id: string;
  serviceType: "research" | "advising";
  requestType: string;
  title: string;
  description: string;
  context: string;
  provider: {
    id: string;
    name: string;
    role: "analyst" | "advisor";
    firm: string;
    specialty?: string;
  };
  status: "pending" | "accepted" | "in_progress" | "completed" | "declined";
  priority: "low" | "medium" | "high" | "urgent";
  requestedDate: string;
  dueDate?: string;
  completedDate?: string;
  focusAreas: string[];
  questions?: string[];
  portfolioContext?: {
    totalValue?: string;
    topHoldings?: string[];
    concerns?: string[];
  };
  fee?: {
    amount: string;
    type: "fixed" | "hourly" | "aum_based";
    billing: "one_time" | "monthly" | "quarterly" | "annual";
  };
  messages?: Message[];
  deliverables?: string[];
  documents?: ResearchDocument[];
  researchContent?: string;
}

interface Message {
  id: string;
  sender: "investor" | "provider";
  senderName: string;
  content: string;
  timestamp: string;
}



const sampleRequests: ServiceRequest[] = [
  {
    id: "req1",
    serviceType: "research",
    requestType: "portfolio_review",
    title: "Portfolio Review - Tech Heavy Allocation",
    description: "Need comprehensive review of my technology-heavy portfolio with rebalancing recommendations.",
    context: "65% tech allocation, concerned about concentration risk. Looking for diversification strategies.",
    provider: {
      id: "ana1",
      name: "Sarah Chen",
      role: "analyst",
      firm: "Chen Analytics",
      specialty: "Technology",
    },
    status: "in_progress",
    priority: "high",
    requestedDate: "2025-10-14",
    dueDate: "2025-10-21",
    focusAreas: ["Technology", "Risk Management", "Diversification"],
    questions: [
      "Should I reduce NVDA position (12% of portfolio)?",
      "Best defensive tech alternatives?",
      "Target allocation percentages?",
    ],
    portfolioContext: {
      totalValue: "$850,000",
      topHoldings: ["AAPL", "MSFT", "NVDA"],
      concerns: ["Concentration risk", "High valuations"]
    },
    fee: {
      amount: "$2,500",
      type: "fixed",
      billing: "one_time",
    },
    deliverables: [
      "Portfolio analysis report",
      "Risk assessment",
      "Rebalancing recommendations",
      "Tax-efficient transition plan",
    ],
  },
  {
    id: "req2",
    serviceType: "advising",
    requestType: "financial_plan",
    title: "Comprehensive Financial Plan",
    description: "Need complete financial plan covering retirement, college funding, and estate planning.",
    context: "Married, 2 kids (ages 8, 12), $1.2M portfolio, planning to retire at 60. Need holistic strategy.",
    provider: {
      id: "adv2",
      name: "Jennifer Martinez",
      role: "advisor",
      firm: "Martinez Financial Advisors",
    },
    status: "accepted",
    priority: "medium",
    requestedDate: "2025-10-10",
    dueDate: "2025-11-10",
    focusAreas: ["Retirement Planning", "College Funding", "Estate Planning", "Tax Strategy"],
    questions: [
      "How much should I save for retirement?",
      "Best college savings strategy (529 vs. other)?",
      "Do I need life insurance adjustments?",
      "Estate planning priorities?",
    ],
    portfolioContext: {
      totalValue: "$1,200,000",
      topHoldings: ["VTI", "VXUS", "BND"],
      concerns: ["Retirement readiness", "Tax efficiency"]
    },
    fee: {
      amount: "$4,000",
      type: "fixed",
      billing: "one_time",
    },
    deliverables: [
      "Comprehensive financial plan (50+ pages)",
      "Retirement projections",
      "College funding strategy",
      "Estate planning recommendations",
      "Tax optimization plan",
      "Insurance review",
    ],
  },
  {
    id: "req3",
    serviceType: "research",
    requestType: "stock_research",
    title: "Tesla (TSLA) Deep Dive Analysis",
    description: "Detailed analysis of Tesla - is it a buy at current valuation?",
    context: "Considering adding EV exposure to portfolio. Want expert analysis on TSLA vs. competitors.",
    provider: {
      id: "ana1",
      name: "Sarah Chen",
      role: "analyst",
      firm: "Chen Analytics",
    },
    status: "completed",
    priority: "medium",
    requestedDate: "2025-10-05",
    completedDate: "2025-10-08",
    focusAreas: ["Electric Vehicles", "Valuation", "Competition"],
    portfolioContext: {
      totalValue: "$850,000",
      topHoldings: ["AAPL", "MSFT", "NVDA"],
      concerns: ["Concentration risk", "High valuations"]
    },
    fee: {
      amount: "$750",
      type: "fixed",
      billing: "one_time",
    },
    deliverables: [
      "Company analysis report",
      "Valuation models (DCF, comparables)",
      "Competitive analysis",
      "Buy/Hold/Sell recommendation",
    ],
  },
  {
    id: "req4",
    serviceType: "advising",
    requestType: "investment_management",
    title: "Ongoing Investment Management Services",
    description: "Looking for professional management of my investment portfolio.",
    context: "Too busy to manage investments actively. Need expert oversight and regular rebalancing.",
    provider: {
      id: "adv1",
      name: "Michael Torres",
      role: "advisor",
      firm: "Torres Wealth Management",
    },
    status: "pending",
    priority: "high",
    requestedDate: "2025-10-16",
    focusAreas: ["Portfolio Management", "Asset Allocation", "Rebalancing", "Tax Efficiency"],
    portfolioContext: {
      totalValue: "$1,200,000",
      topHoldings: ["VTI", "VXUS", "BND"],
      concerns: ["Retirement readiness", "Tax efficiency"]
    },
    fee: {
      amount: "1.00% ($12,000 annually)",
      type: "aum_based",
      billing: "quarterly",
    },
    deliverables: [
      "Quarterly portfolio reviews",
      "Ongoing rebalancing",
      "Tax-loss harvesting",
      "Performance reporting",
      "24/7 account access",
    ],
  },
];

export function InvestorServiceRequests() {
  const { userRole } = useAuth();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isFetchingRequests, setIsFetchingRequests] = useState(false);

  // Fetch all requests for the investor
  useEffect(() => {
    const fetchRequests = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      try {
        setIsFetchingRequests(true);
        const response = await researchAdvisingService.getRequests(userId);
        const data = response.data || response;
        
        if (Array.isArray(data)) {
          // Map backend DTO to frontend interface
          const mappedRequests: ServiceRequest[] = data.map((req: any) => ({
            id: req.id,
            serviceType: (req.categoryType?.toLowerCase() === "advising" ? "advising" : "research") as "advising" | "research",
            requestType: req.requestType,
            title: req.title,
            description: req.description,
            context: req.situationContext || "",
            provider: {
              id: req.recipientId,
              name: req.recipientName,
              role: (req.recipeintRole?.toLowerCase() === "advisor" ? "advisor" : "analyst") as "advisor" | "analyst",
              firm: req.recipientFirm || "WealthBrainAI Partner",
            },
            status: (req.status?.toLowerCase() || "pending") as any,
            priority: (req.priority?.toLowerCase() || "medium") as any,
            requestedDate: req.requestedDate || req.createdAt,
            dueDate: req.dueDate,
            completedDate: req.completedDate,
            focusAreas: Array.isArray(req.primaryGoals) 
              ? req.primaryGoals.map((g: any) => g.value || g.label) 
              : [],
            questions: typeof req.questions === 'string' 
              ? req.questions.split('\n').filter((q: string) => q.trim() !== '') 
              : (Array.isArray(req.questions) ? req.questions : []),
            deliverables: typeof req.deliverables === 'string'
              ? req.deliverables.split('\n').filter((d: string) => d.trim() !== '')
              : (Array.isArray(req.deliverables) ? req.deliverables : []),
            portfolioContext: req.portfolioContext || (req.portfolioValue ? { totalValue: req.portfolioValue } : undefined),
            fee: req.feeStructure ? {
              amount: `$${req.feeStructure.amount}`,
              type: (req.feeStructure.pricingInfo?.type?.toLowerCase() || "fixed") as any,
              billing: (req.feeStructure.frequency?.toLowerCase() || "one_time") as any,
            } : undefined,
            messages: Array.isArray(req.messages) ? req.messages : [],
            documents: Array.isArray(req.documents) ? req.documents : [],
          }));
          setRequests(mappedRequests);
        }
      } catch (error) {
        console.error("Error fetching requests:", error);
        toast.error("Failed to load your requests");
      } finally {
        setIsFetchingRequests(false);
      }
    };

    fetchRequests();
  }, []);

  const [sharePortfolio, setSharePortfolio] = useState(false);
  const [shareProfile, setShareProfile] = useState(false);
  const [requestServiceType, setRequestServiceType] = useState<"research" | "advising">("research");
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterService, setFilterService] = useState<string>("all");
  const [connectedAdvisors, setConnectedAdvisors] = useState<ServiceProvider[]>([]);
  const [isFetchingAdvisors, setIsFetchingAdvisors] = useState(false);
  const [connectedAnalysts, setConnectedAnalysts] = useState<ServiceProvider[]>([]);
  const [isFetchingAnalysts, setIsFetchingAnalysts] = useState(false);
  const [selectedAnalystId, setSelectedAnalystId] = useState<string>("");
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string>("");
  const [researchFeeAmount, setResearchFeeAmount] = useState<string>("");
  const [advisingFeeAmount, setAdvisingFeeAmount] = useState<string>("");
  const [researchFeeType, setResearchFeeType] = useState<string>("");
  const [advisingFeeType, setAdvisingFeeType] = useState<string>("");
  const [advisingPaymentTerm, setAdvisingPaymentTerm] = useState<string>("");
  const [researchPaymentTerm, setResearchPaymentTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  
  // Form states
  const [requestTitle, setRequestTitle] = useState("");
  const [requestPriority, setRequestPriority] = useState("medium");
  const [requestDescription, setRequestDescription] = useState("");
  const [requestSituation, setRequestSituation] = useState("");
  const [totalValue, setTotalValue] = useState("");
  const [topHoldings, setTopHoldings] = useState("");
  const [concerns, setConcerns] = useState("");
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [researchType, setResearchType] = useState("");
  const [advisingType, setAdvisingType] = useState("");
  const [questions, setQuestions] = useState("");
  const [deliverables, setDeliverables] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isUploadingAdditional, setIsUploadingAdditional] = useState(false);
  const [isAdditionalUploadOpen, setIsAdditionalUploadOpen] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState<any[]>([]);

  const fetchUpcomingMeetings = async () => {
    const currentUserId = localStorage.getItem("userId");
    if (!selectedRequest || !currentUserId) return;
    
    const participantId = selectedRequest.provider.id;
      
    if (!participantId) return;

    try {
      // Fetch both PENDING and ACCEPTED meetings
      const [pendingRes, acceptedRes] = await Promise.all([
        meetingService.getMyMeetingsWithParticipant(currentUserId, participantId, "PENDING", false),
        meetingService.getMyMeetingsWithParticipant(currentUserId, participantId, "ACCEPTED", false)
      ]);
      
      const pMeetings = pendingRes.data || (Array.isArray(pendingRes) ? pendingRes : []);
      const aMeetings = acceptedRes.data || (Array.isArray(acceptedRes) ? acceptedRes : []);
      
      setUpcomingMeetings([...pMeetings, ...aMeetings].sort((a, b) => 
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      ));
    } catch (error) {
      console.error("Error fetching upcoming meetings:", error);
    }
  };

  useEffect(() => {
    fetchUpcomingMeetings();
  }, [selectedRequest]);

  const handleUpdateMeetingStatus = async (meetingId: string, userId:any, status: string) => {
    const currentUserId = localStorage.getItem("userId");
    if (!currentUserId) return;

    try {
      await meetingService.updateMeetingStatus(meetingId, userId, status);
      toast.success(`Meeting ${status.toLowerCase()}ed successfully`);
      fetchUpcomingMeetings();
    } catch (error) {
      console.error("Error updating meeting status:", error);
      toast.error(`Failed to ${status.toLowerCase()} meeting`);
    }
  };

  const handleUploadAdditionalFiles = async (files: File[]) => {
    if (!selectedRequest || files.length === 0) return;
    
    const userId = localStorage.getItem("userId");
    const userName = localStorage.getItem("fullName") || "Investor";

    try {
      setIsUploadingAdditional(true);
      const newDocuments: ResearchDocument[] = [];
      
      for (const file of files) {
        const uploadResult = await uploadService.uploadFile(file, "research-requests-additional");
        newDocuments.push({
          requestId: selectedRequest.id,
          uploadedByUserId: userId || "",
          uploadedByName: userName,
          documentName: file.name,
          documentType: "ADDITIONAL_ATTACHMENT",
          fileUrl: uploadResult.url,
          fileSizeBytes: file.size,
          fileMimeType: file.type,
          isSensitive: false,
          uploadedAt: new Date().toISOString(),
          description: `Additional attachment for request: ${selectedRequest.title}`
        } as any);
      }

      await researchAdvisingService.addDocuments(selectedRequest.id, newDocuments);
      toast.success("Additional files uploaded successfully");
      setIsAdditionalUploadOpen(false);
      
      // Refresh request
      const response = await researchAdvisingService.getRequests(userId || "");
      const data = response.data || response;
      if (Array.isArray(data)) {
        // Map backend DTO to frontend interface
        const mappedRequests: ServiceRequest[] = data.map((req: any) => ({
          id: req.id,
          serviceType: (req.categoryType?.toLowerCase() === "advising" ? "advising" : "research") as "advising" | "research",
          requestType: req.requestType,
          title: req.title,
          description: req.description,
          context: req.situationContext || "",
          provider: {
            id: req.recipientId,
            name: req.recipientName,
            role: (req.recipeintRole?.toLowerCase() === "advisor" ? "advisor" : "analyst") as "advisor" | "analyst",
            firm: req.recipientFirm || "WealthBrainAI Partner",
          },
          status: (req.status?.toLowerCase() || "pending") as any,
          priority: (req.priority?.toLowerCase() || "medium") as any,
          requestedDate: req.requestedDate || req.createdAt,
          dueDate: req.dueDate,
          completedDate: req.completedDate,
          focusAreas: Array.isArray(req.primaryGoals) 
            ? req.primaryGoals.map((g: any) => g.value || g.label) 
            : [],
          questions: typeof req.questions === 'string' 
            ? req.questions.split('\n').filter((q: string) => q.trim() !== '') 
            : (Array.isArray(req.questions) ? req.questions : []),
          deliverables: typeof req.deliverables === 'string'
            ? req.deliverables.split('\n').filter((d: string) => d.trim() !== '')
            : (Array.isArray(req.deliverables) ? req.deliverables : []),
          portfolioContext: req.portfolioContext || (req.portfolioValue ? { totalValue: req.portfolioValue } : undefined),
          fee: req.feeStructure ? {
            amount: `$${req.feeStructure.amount}`,
            type: (req.feeStructure.pricingInfo?.type?.toLowerCase() || "fixed") as any,
            billing: (req.feeStructure.frequency?.toLowerCase() || "one_time") as any,
          } : undefined,
          messages: Array.isArray(req.messages) ? req.messages : [],
          documents: Array.isArray(req.documents) ? req.documents : [],
          researchContent: req.researchContent,
        }));
        setRequests(mappedRequests);
        const updated = mappedRequests.find(r => r.id === selectedRequest.id);
        if (updated) setSelectedRequest(updated);
      }
    } catch (error: any) {
      console.error("Error uploading additional files:", error);
      toast.error(error.message || "Failed to upload additional files");
    } finally {
      setIsUploadingAdditional(false);
    }
  };

  // Fetch connected service providers when the respective tab is selected
  useEffect(() => {
    const fetchConnectedProviders = async () => {
      if (!isRequestDialogOpen) return;

      const userId = localStorage.getItem("userId");
      if (!userId) {
        console.error("No userId found in localStorage");
        return;
      }

      try {
        if (requestServiceType === "advising") {
          setIsFetchingAdvisors(true);
          const response = await advisorService.getConnectedOrEngagedAdvisors(userId);
          const data = response.data || response;
          if (Array.isArray(data)) {
            setConnectedAdvisors(data);
          }
        } else if (requestServiceType === "research") {
          setIsFetchingAnalysts(true);
          const response = await analystService.getConnectedOrEngagedAnalysts(userId);
          const data = response.data || response;
          if (Array.isArray(data)) {
            setConnectedAnalysts(data);
          }
        }
      } catch (error) {
        console.error(`Error fetching connected ${requestServiceType}s:`, error);
        toast.error(`Failed to load connected ${requestServiceType === "research" ? "analysts" : "advisors"}`);
      } finally {
        setIsFetchingAdvisors(false);
        setIsFetchingAnalysts(false);
      }
    };

    fetchConnectedProviders();
  }, [isRequestDialogOpen, requestServiceType]);

  // Reset selections when dialog closes or service type changes
  useEffect(() => {
    setSelectedAnalystId("");
    setSelectedAdvisorId("");
    setResearchFeeAmount("");
    setAdvisingFeeAmount("");
    setResearchFeeType("");
    setAdvisingFeeType("");
    setAdvisingPaymentTerm("");
    setResearchPaymentTerm("");
    setRequestTitle("");
    setRequestPriority("medium");
    setRequestDescription("");
    setRequestSituation("");
    setTotalValue("");
    setTopHoldings("");
    setConcerns("");
    setSelectedGoals([]);
    setResearchType("");
    setAdvisingType("");
    setQuestions("");
    setDeliverables("");
    setUploadedFiles([]);
  }, [isRequestDialogOpen, requestServiceType]);

  const handleCreateRequest = async () => {
    const userId = localStorage.getItem("userId");
    const userName = localStorage.getItem("fullName") || "Investor";
    const userRoleValue = localStorage.getItem("userRole") || "INVESTOR";

    if (!userId) {
      toast.error("User not authenticated");
      return;
    }

    const isAdvising = requestServiceType === "advising";
    const selectedProviderId = isAdvising ? selectedAdvisorId : selectedAnalystId;
    
    if (!selectedProviderId) {
      toast.error(`Please select a ${isAdvising ? "financial advisor" : "analyst"}`);
      return;
    }

    const selectedProvider = isAdvising 
      ? connectedAdvisors.find(a => a.id === selectedAdvisorId)
      : connectedAnalysts.find(a => a.id === selectedAnalystId);

    if (!requestTitle || !requestDescription) {
      toast.error("Please fill in the request title and description");
      return;
    }

    setIsLoading(true);

    try {
      // 1. Upload files to S3 first
      const uploadedDocuments = [];
      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          try {
            const { url } = await uploadService.uploadFile(file, 'research');
            uploadedDocuments.push({
              uploadedByUserId: userId,
              uploadedByName: userName,
              documentName: file.name,
              documentType: "research_request",
              fileUrl: url,
              fileSizeBytes: file.size,
              fileMimeType: file.type,
              isSensitive: false,
              uploadedAt: new Date().toISOString(),
              description: requestTitle
            });
          } catch (uploadErr) {
            console.error(`Failed to upload file ${file.name}:`, uploadErr);
            // We can decide whether to fail everything or just skip this file
            // For now, let's throw to be safe
            throw new Error(`Failed to upload ${file.name}`);
          }
        }
      }

      const feeType = isAdvising ? advisingFeeType : researchFeeType;
      const feeAmount = isAdvising ? advisingFeeAmount : researchFeeAmount;
      const paymentTerm = isAdvising ? advisingPaymentTerm : (researchPaymentTerm || "ONE_TIME");

      const feeStructure = {
        pricingInfo: {
          type: feeType,
          range: "As agreed",
          details: `Payment term: ${paymentTerm}`
        },
        amount: parseFloat(feeAmount) || 0,
        frequency: paymentTerm
      };

      const requestDto: ResearchAdvisingRequest = {
        title: requestTitle,
        requestType: isAdvising ? advisingType : researchType,
        categoryType: isAdvising ? "ADVISING" : "RESEARCH",
        description: requestDescription,
        requesterId: userId,
        requesterName: userName,
        requesterRole: userRoleValue,
        recipientId: selectedProviderId,
        recipientName: selectedProvider?.name || "",
        recipientRole: selectedProvider?.role?.toUpperCase() || "",
        status: "pending",
        priority: requestPriority,
        requestedDate: new Date().toISOString(),
        dueDate: null,
        acceptedDate: null,
        completedDate: null,
        focusAreas: selectedGoals,
        situationContext: requestSituation,
        portfolioContext: {
          totalValue,
          topHoldings: topHoldings.split(',').map(s => s.trim()).filter(s => s !== ""),
          concerns: concerns.split(',').map(s => s.trim()).filter(s => s !== "")
        },
        questions: questions,
        deliverables: deliverables,
        hasCurrentAdvisor: false,
        engagementSigned: false,
        engagementSignedAt: null,
        sharePortfolio,
        shareProfile,
        documents: uploadedDocuments,
        feeStructure,
        primaryGoals: selectedGoals.map(goal => ({ label: goal, value: goal })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await researchAdvisingService.createRequest(requestDto);
      
      toast.success(`${isAdvising ? "Advising" : "Research"} request sent successfully!`);
      setIsRequestDialogOpen(false);
      // Refresh requests list could be triggered here
    } catch (error: any) {
      console.error('Error creating request:', error);
      toast.error(error.message || "Failed to submit request");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedRequest) return;

    const message: Message = {
      id: `m${Date.now()}`,
      sender: "investor",
      senderName: "You",
      content: newMessage,
      timestamp: new Date().toLocaleString(),
    };

    const updatedRequest = {
      ...selectedRequest,
      messages: [...(selectedRequest.messages || []), message],
    };

    setRequests(requests.map((r) => (r.id === selectedRequest.id ? updatedRequest : r)));
    setSelectedRequest(updatedRequest);
    setNewMessage("");
    toast.success("Message sent");
  };

  const handleDownload = async (fileUrl: string) => {
    try {
      await uploadService.downloadFile(fileUrl);
    } catch (error: any) {
      toast.error(error.message || "Failed to download file");
    }
  };

  const getStatusColor = (status: ServiceRequest["status"]) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
      case "accepted":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      case "in_progress":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300";
      case "completed":
        return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      case "declined":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      case "high":
        return "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300";
      case "medium":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
      case "low":
        return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const filteredRequests = requests.filter((req) => {
    if (filterStatus !== "all" && req.status !== filterStatus) return false;
    if (filterService !== "all" && req.serviceType !== filterService) return false;
    if (
      searchQuery &&
      !req.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !req.description.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const researchRequests = requests.filter((r) => r.serviceType === "research");
  const advisingRequests = requests.filter((r) => r.serviceType === "advising");
  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const activeCount = requests.filter(
    (r) => r.status === "accepted" || r.status === "in_progress"
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl">Professional Services</h2>
          <p className="text-muted-foreground">
            Request research from analysts or advising services from financial advisors
          </p>
        </div>
        <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusCircle className="mr-2 h-4 w-4" />
              New Service Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Request Professional Services</DialogTitle>
              <DialogDescription>
                Get expert help from analysts or financial advisors
              </DialogDescription>
            </DialogHeader>

            <Tabs
              value={requestServiceType}
              onValueChange={(v) => setRequestServiceType(v as "research" | "advising")}
              className="mt-4"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="research" className="gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Research Request (Analyst)
                </TabsTrigger>
                <TabsTrigger value="advising" className="gap-2">
                  <Briefcase className="h-4 w-4" />
                  Advising Request (Advisor)
                </TabsTrigger>
              </TabsList>

              {/* Research Request Form */}
              <TabsContent value="research" className="space-y-4 mt-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Research Services
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        Get professional investment research, portfolio analysis, and market insights
                        from certified analysts. Typical fees: $500 - $5,000 per request.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Analyst</Label>
                  <Select 
                    key="research-analyst-select"
                    value={selectedAnalystId}
                    onValueChange={setSelectedAnalystId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an analyst">
                        {selectedAnalystId ? connectedAnalysts.find(a => a.id === selectedAnalystId)?.name : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {isFetchingAnalysts ? (
                        <div className="p-4 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                          <Clock className="h-4 w-4 animate-spin" />
                          Loading connected analysts...
                        </div>
                      ) : connectedAnalysts.length > 0 ? (
                        connectedAnalysts.map((analyst) => (
                          <SelectItem key={analyst.id} value={analyst.id}>
                            <div className="flex items-start gap-3 py-2">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback>
                                  {analyst.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium flex items-center">
                                    {analyst.name}
                                    {analyst.isConnected && (
                                      <span className="ml-2 text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100 uppercase tracking-wider">
                                        Connected
                                      </span>
                                    )}
                                    {analyst.isEngaged && (
                                      <span className="ml-2 text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 uppercase tracking-wider">
                                        Engaged
                                      </span>
                                    )}
                                  </p>
                                  <Badge variant="outline" className="text-xs">
                                    ⭐ {analyst.rating} ({analyst.reviewCount})
                                  </Badge>
                                  {analyst.availability === "limited" && (
                                    <Badge variant="secondary" className="text-xs">
                                      Limited availability
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">{analyst.firm}</p>
                                <p className="text-xs text-muted-foreground">
                                  {analyst.specialties?.slice(0, 3).join(", ")}
                                </p>
                                <div className="mt-2 space-y-1">
                                  {analyst.pricing?.map((p, idx) => (
                                    <div key={idx} className="flex flex-col border-t border-border/40 pt-1 mt-1 first:border-t-0 first:pt-0 first:mt-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-[10px] font-semibold text-primary/80 uppercase tracking-tight">{p.type}</span>
                                        <span className="text-xs font-bold text-green-600 dark:text-green-400">{p.range}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground italic line-clamp-1">{p.details}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          No connected analysts found. Connect with analysts in the matching section first.
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Research Type</Label>
                  <Select 
                    key="research-type-select"
                    value={researchType}
                    onValueChange={setResearchType}
                  >
                  <SelectTrigger>
                    <SelectValue placeholder="What type of research do you need?" />
                  </SelectTrigger>
                  <SelectContent>
                    {RESEARCH_REQUEST_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-3 py-1">
                            <Icon className="h-4 w-4" />
                            <div>
                              <p className="font-medium">{type.label}</p>
                              <p className="text-xs text-muted-foreground">
                                Typical fee: {type.fee}
                              </p>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                  </Select>                </div>

                <div className="space-y-2">
                  <Label>Request Title</Label>
                  <Input 
                    placeholder="e.g., Portfolio Review - Tech Heavy Allocation" 
                    value={requestTitle}
                    onChange={(e) => setRequestTitle(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Request Priority</Label>
                  <Select 
                    value={requestPriority}
                    onValueChange={setRequestPriority}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - No rush</SelectItem>
                      <SelectItem value="medium">Medium - Within 2 weeks</SelectItem>
                      <SelectItem value="high">High - Within 1 week</SelectItem>
                      <SelectItem value="urgent">Urgent - ASAP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Describe what you're looking for..."
                    rows={3}
                    value={requestDescription}
                    onChange={(e) => setRequestDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Context & Background</Label>
                  <Textarea
                    placeholder="Provide context about your situation, concerns, or objectives..."
                    rows={3}
                    value={requestSituation}
                    onChange={(e) => setRequestSituation(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Specific Questions (Optional)</Label>
                  <Textarea
                    placeholder="List specific questions you'd like answered..."
                    rows={3}
                    value={questions}
                    onChange={(e) => setQuestions(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected Deliverables (Optional)</Label>
                  <Textarea
                    placeholder="List expected deliverables (e.g., PDF report, Excel model)..."
                    rows={3}
                    value={deliverables}
                    onChange={(e) => setDeliverables(e.target.value)}
                  />
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold">Portfolio Context (Optional)</h4>
                  <div className="space-y-2">
                    <Label>Total Portfolio Value</Label>
                    <Input 
                      placeholder="e.g., $500,000" 
                      value={totalValue}
                      onChange={(e) => setTotalValue(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Top Holdings (Optional)</Label>
                    <Input 
                      placeholder="e.g., AAPL, MSFT, NVDA (comma separated)" 
                      value={topHoldings}
                      onChange={(e) => setTopHoldings(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Key Concerns (Optional)</Label>
                    <Textarea 
                      placeholder="e.g., Concentration risk, lack of international exposure" 
                      rows={2}
                      value={concerns}
                      onChange={(e) => setConcerns(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold">Fee Structure</h4>
                  <div className="space-y-2">
                    <Label>Preferred Fee Structure</Label>
                    <Select 
                      key="research-fee-structure-select"
                      value={researchFeeType}
                      onValueChange={setResearchFeeType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                      <SelectContent>
                        {ANALYST_FEE_TYPES.map((fee) => (
                          <SelectItem key={fee.value} value={fee.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{fee.label}</span>
                              <span className="text-[10px] text-muted-foreground line-clamp-1">
                                {fee.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {researchFeeType && 
                  researchFeeType !== "flexible" && 
                  researchFeeType !== "engagement_agreement" && (
                   <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-2">
                       <Label>Proposed Fee Amount ($)</Label>
                       <Input 
                         type="number" 
                         placeholder="e.g. 500" 
                         value={researchFeeAmount}
                         onChange={(e) => setResearchFeeAmount(e.target.value)}
                       />
                     </div>
                     <div className="space-y-2">
                        <Label>Payment Term</Label>
                        <Select 
                          value={researchPaymentTerm}
                          onValueChange={setResearchPaymentTerm}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select term" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UPFRONT">Upfront</SelectItem>
                            <SelectItem value="MONTHLY">Monthly</SelectItem>
                            <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                            <SelectItem value="ANNUALLY">Annually</SelectItem>
                            <SelectItem value="ON_COMPLETION">On Completion</SelectItem>
                            <SelectItem value="MILESTONE_BASED">Milestone-based</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                   </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Primary Goals (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {FINANCIAL_SPECIALIZATIONS.map((goal) => (
                      <div key={`research-${goal}`} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`research-goal-${goal}`} 
                          checked={selectedGoals.includes(goal)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedGoals([...selectedGoals, goal]);
                            } else {
                              setSelectedGoals(selectedGoals.filter(g => g !== goal));
                            }
                          }}
                        />
                        <label htmlFor={`research-goal-${goal}`} className="text-sm cursor-pointer">
                          {goal}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 py-2 border-t">
                  {(userRole === 'INVESTOR' || !userRole) && (
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="share-portfolio-research" 
                        checked={sharePortfolio}
                        onCheckedChange={(checked) => setSharePortfolio(!!checked)}
                      />
                      <Label htmlFor="share-portfolio-research" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Share Portfolio
                      </Label>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="share-profile-research" 
                      checked={shareProfile}
                      onCheckedChange={(checked) => setShareProfile(!!checked)}
                    />
                    <Label htmlFor="share-profile-research" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Share Profile details
                    </Label>
                  </div>
                </div>

                <FileUploadSection
                  id="research-service-upload"
                  helpText="Attach portfolio statements, financial documents, or any relevant files to help the analyst understand your request."
                  onFilesChange={(files) => setUploadedFiles(files)}
                />
              </TabsContent>

              {/* Advising Request Form */}
              <TabsContent value="advising" className="space-y-4 mt-4">
                <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-900 dark:text-green-100">
                        Financial Advising Services
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        Get comprehensive financial planning and investment management from certified
                        advisors. Fees typically 0.75%-1.25% AUM annually or $1,000-$10,000 for
                        planning services. Flat fee per request $0-$200.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Financial Advisor</Label>
                  <Select 
                    key="advising-advisor-select"
                    value={selectedAdvisorId}
                    onValueChange={setSelectedAdvisorId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a financial advisor">
                        {selectedAdvisorId ? connectedAdvisors.find(a => a.id === selectedAdvisorId)?.name : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {isFetchingAdvisors ? (
                        <div className="p-4 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                          <Clock className="h-4 w-4 animate-spin" />
                          Loading connected advisors...
                        </div>
                      ) : connectedAdvisors.length > 0 ? (
                        connectedAdvisors.map((advisor) => (
                          <SelectItem key={advisor.id} value={advisor.id}>
                            <div className="flex items-start gap-3 py-2">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback>
                                  {advisor.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium flex items-center">
                                    {advisor.name}
                                    {advisor.isConnected && (
                                      <span className="ml-2 text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100 uppercase tracking-wider">
                                        Connected
                                      </span>
                                    )}
                                    {advisor.isEngaged && (
                                      <span className="ml-2 text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 uppercase tracking-wider">
                                        Engaged
                                      </span>
                                    )}
                                  </p>
                                  <Badge variant="outline" className="text-xs">
                                    ⭐ {advisor.rating} ({advisor.reviewCount})
                                  </Badge>
                                  {advisor.availability === "limited" && (
                                    <Badge variant="secondary" className="text-xs">
                                      Limited availability
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">{advisor.firm}</p>
                                <p className="text-xs text-muted-foreground">
                                  {advisor.specialties?.slice(0, 3).join(", ")}
                                </p>
                                <div className="mt-2 space-y-1">
                                  {advisor.pricing?.map((p, idx) => (
                                    <div key={idx} className="flex flex-col border-t border-border/40 pt-1 mt-1 first:border-t-0 first:pt-0 first:mt-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-[10px] font-semibold text-primary/80 uppercase tracking-tight">{p.type}</span>
                                        <span className="text-xs font-bold text-green-600 dark:text-green-400">{p.range}</span>
                                      </div>
                                      <p className="text-[10px] text-muted-foreground italic line-clamp-1">{p.details}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          No connected advisors found. Connect with advisors in the matching section first.
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select 
                    key="advising-service-type-select"
                    value={advisingType}
                    onValueChange={setAdvisingType}
                  >
                  <SelectTrigger>
                    <SelectValue placeholder="What service do you need?" />
                  </SelectTrigger>
                  <SelectContent>
                    {ADVISING_REQUEST_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-3 py-1">
                            <Icon className="h-4 w-4" />
                            <div>
                              <p className="font-medium">{type.label}</p>
                              <p className="text-xs text-muted-foreground">
                                Typical fee: {type.fee}
                              </p>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                  </Select>                </div>

                <div className="space-y-2">
                  <Label>Request Title</Label>
                  <Input 
                    placeholder="e.g., Comprehensive Financial Plan" 
                    value={requestTitle}
                    onChange={(e) => setRequestTitle(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Request Priority</Label>
                  <Select 
                    value={requestPriority}
                    onValueChange={setRequestPriority}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - No rush</SelectItem>
                      <SelectItem value="medium">Medium - Within 2 weeks</SelectItem>
                      <SelectItem value="high">High - Within 1 week</SelectItem>
                      <SelectItem value="urgent">Urgent - ASAP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Describe the financial advising services you need..."
                    rows={3}
                    value={requestDescription}
                    onChange={(e) => setRequestDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Your Situation</Label>
                  <Textarea
                    placeholder="Tell us about your financial situation, goals, and concerns (e.g., age, family status, retirement goals, specific challenges)..."
                    rows={4}
                    value={requestSituation}
                    onChange={(e) => setRequestSituation(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Specific Questions (Optional)</Label>
                  <Textarea
                    placeholder="List specific questions you'd like answered..."
                    rows={3}
                    value={questions}
                    onChange={(e) => setQuestions(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected Deliverables (Optional)</Label>
                  <Textarea
                    placeholder="List expected deliverables (e.g., PDF report, Excel model)..."
                    rows={3}
                    value={deliverables}
                    onChange={(e) => setDeliverables(e.target.value)}
                  />
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold">Portfolio Context (Optional)</h4>
                  <div className="space-y-2">
                    <Label>Total Portfolio Value</Label>
                    <Input 
                      placeholder="e.g., $1,200,000" 
                      value={totalValue}
                      onChange={(e) => setTotalValue(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Top Holdings (Optional)</Label>
                    <Input 
                      placeholder="e.g., VTI, VXUS, BND (comma separated)" 
                      value={topHoldings}
                      onChange={(e) => setTopHoldings(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Key Concerns (Optional)</Label>
                    <Textarea 
                      placeholder="e.g., Retirement readiness, tax optimization" 
                      rows={2}
                      value={concerns}
                      onChange={(e) => setConcerns(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold">Fee Structure</h4>
                  <div className="space-y-2">
                    <Label>Preferred Fee Structure</Label>
                    <Select 
                      key="advising-fee-structure-select"
                      value={advisingFeeType}
                      onValueChange={setAdvisingFeeType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                      <SelectContent>
                        {ADVISOR_FEE_TYPES.map((fee) => (
                          <SelectItem key={fee.value} value={fee.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{fee.label}</span>
                              <span className="text-[10px] text-muted-foreground line-clamp-1">
                                {fee.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {advisingFeeType && 
                   advisingFeeType !== "flexible" && 
                   advisingFeeType !== "engagement_agreement" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Proposed Fee Amount ($)</Label>
                        <Input 
                          type="number" 
                          placeholder="e.g. 1000" 
                          value={advisingFeeAmount}
                          onChange={(e) => setAdvisingFeeAmount(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Payment Term</Label>
                        <Select 
                          value={advisingPaymentTerm}
                          onValueChange={setAdvisingPaymentTerm}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select term" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UPFRONT">Upfront</SelectItem>
                            <SelectItem value="MONTHLY">Monthly</SelectItem>
                            <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                            <SelectItem value="ANNUALLY">Annually</SelectItem>
                            <SelectItem value="ON_COMPLETION">On Completion</SelectItem>
                            <SelectItem value="MILESTONE_BASED">Milestone-based</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <Label>Primary Goals (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {FINANCIAL_SPECIALIZATIONS.map((goal) => (
                      <div key={`advising-${goal}`} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`goal-${goal}`} 
                          checked={selectedGoals.includes(goal)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedGoals([...selectedGoals, goal]);
                            } else {
                              setSelectedGoals(selectedGoals.filter(g => g !== goal));
                            }
                          }}
                        />
                        <label htmlFor={`goal-${goal}`} className="text-sm cursor-pointer">
                          {goal}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 py-2 border-t">
                  {(userRole === 'INVESTOR' || !userRole) && (
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="share-portfolio-advising" 
                        checked={sharePortfolio}
                        onCheckedChange={(checked) => setSharePortfolio(!!checked)}
                      />
                      <Label htmlFor="share-portfolio-advising" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Share Portfolio
                      </Label>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="share-profile-advising" 
                      checked={shareProfile}
                      onCheckedChange={(checked) => setShareProfile(!!checked)}
                    />
                    <Label htmlFor="share-profile-advising" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Share Profile details
                    </Label>
                  </div>
                </div>

                <FileUploadSection
                  id="advising-service-upload"
                  helpText="Attach financial documents, tax returns, or any relevant files to help the advisor understand your situation."
                  onFilesChange={(files) => setUploadedFiles(files)}
                />
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleCreateRequest} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{requests.length}</div>
            <p className="text-xs text-muted-foreground">All service requests</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Research Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{researchRequests.length}</div>
            <p className="text-xs text-muted-foreground">From analysts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Advising Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{advisingRequests.length}</div>
            <p className="text-xs text-muted-foreground">From advisors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{activeCount}</div>
            <p className="text-xs text-muted-foreground">In progress</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterService} onValueChange={setFilterService}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                <SelectItem value="research">Research Only</SelectItem>
                <SelectItem value="advising">Advising Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests List and Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Requests List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>My Requests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No requests found</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setIsRequestDialogOpen(true)}
                  >
                    Create First Request
                  </Button>
                </div>
              ) : (
                filteredRequests.map((request) => (
                  <div
                    key={request.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all hover:bg-muted/50 ${
                      selectedRequest?.id === request.id ? "bg-muted border-primary" : ""
                    }`}
                    onClick={() => setSelectedRequest(request)}
                  >
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div
                          className={`p-2 rounded-lg ${
                            request.serviceType === "research"
                              ? "bg-blue-100 dark:bg-blue-900"
                              : "bg-green-100 dark:bg-green-900"
                          }`}
                        >
                          {request.serviceType === "research" ? (
                            <BarChart3
                              className="h-4 w-4 text-blue-700 dark:text-blue-300"
                            />
                          ) : (
                            <Briefcase
                              className="h-4 w-4 text-green-700 dark:text-green-300"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium line-clamp-2 text-sm mb-1">
                            {request.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mb-1">
                            {request.provider.name} • {request.provider.firm}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={getStatusColor(request.status)} variant="secondary">
                          {request.status.replace("_", " ")}
                        </Badge>
                        {request.fee && (
                          <Badge variant="outline" className="text-xs">
                            💰 {request.fee.amount}
                          </Badge>
                        )}
                      </div>

                      <div className="text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {request.requestedDate}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Request Detail */}
        <div className="lg:col-span-2">
          {selectedRequest ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        className={
                          selectedRequest.serviceType === "research"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                            : "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        }
                      >
                        {selectedRequest.serviceType === "research" ? "Research" : "Advising"}
                      </Badge>
                      <Badge className={getStatusColor(selectedRequest.status)}>
                        {selectedRequest.status.replace("_", " ")}
                      </Badge>
                      {selectedRequest.priority !== "low" && (
                        <Badge className={getPriorityColor(selectedRequest.priority)}>
                          {selectedRequest.priority} priority
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="mb-2">{selectedRequest.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {selectedRequest.description}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Provider Info */}
                <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback>
                      {selectedRequest.provider.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium">{selectedRequest.provider.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedRequest.provider.firm}
                    </p>
                    <Badge
                      variant="outline"
                      className={
                        selectedRequest.provider.role === "analyst"
                          ? "text-blue-700 border-blue-300 dark:text-blue-300"
                          : "text-green-700 border-green-300 dark:text-green-300"
                      }
                    >
                      {selectedRequest.provider.role}
                    </Badge>
                  </div>
                </div>

                <Separator />

                {/* Fee Information */}
                {selectedRequest.fee && (
                  <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-start gap-3">
                      <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-green-900 dark:text-green-100">
                          Fee Structure
                        </p>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Amount</p>
                            <p className="font-medium">{selectedRequest.fee.amount}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Billing</p>
                            <p className="font-medium capitalize">
                              {selectedRequest.fee.billing.replace("_", " ")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Context */}
                <div>
                  <Label className="mb-2">Context</Label>
                  <p className="text-sm text-muted-foreground">{selectedRequest.context}</p>
                </div>

                {/* Focus Areas */}
                {Array.isArray(selectedRequest.focusAreas) && selectedRequest.focusAreas.length > 0 && (
                  <div>
                    <Label className="mb-2">Focus Areas</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedRequest.focusAreas.map((area, idx) => (
                        <Badge key={idx} variant="outline">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specific Questions */}
                {Array.isArray(selectedRequest.questions) &&
                  selectedRequest.questions.length > 0 && (
                    <div>
                      <Label className="mb-2">Specific Questions</Label>
                      <ul className="space-y-2">
                        {selectedRequest.questions.map((question, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <span className="text-primary mt-1">•</span>
                            <span>{question}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Portfolio Context */}
                {selectedRequest.portfolioContext && (
                  <div>
                    <Label className="mb-2">Portfolio Context</Label>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedRequest.portfolioContext.totalValue && (
                        <div>
                          <span className="text-muted-foreground">Total Value:</span>
                          <p className="font-medium">
                            {selectedRequest.portfolioContext.totalValue}
                          </p>
                        </div>
                      )}
                      {selectedRequest.portfolioContext.topHoldings && Array.isArray(selectedRequest.portfolioContext.topHoldings) && (
                        <div>
                          <span className="text-muted-foreground">Top Holdings:</span>
                          <p className="font-medium">
                            {selectedRequest.portfolioContext.topHoldings.join(", ")}
                          </p>
                        </div>
                      )}
                    </div>
                    {Array.isArray(selectedRequest.portfolioContext.concerns) && (
                      <div className="mt-3">
                        <span className="text-muted-foreground text-sm">Concerns:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedRequest.portfolioContext.concerns.map((concern, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              {concern}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Deliverables */}
                {Array.isArray(selectedRequest.deliverables) && selectedRequest.deliverables.length > 0 && (
                  <div>
                    <Label className="mb-2">Expected Deliverables</Label>
                    <ul className="space-y-2">
                      {selectedRequest.deliverables.map((deliverable, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>{deliverable}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Attachments */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Attachments</Label>
                    <Dialog open={isAdditionalUploadOpen} onOpenChange={setIsAdditionalUploadOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 text-xs">
                          <Upload className="h-3 w-3 mr-1" />
                          Upload Additional
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Upload Additional Files</DialogTitle>
                          <DialogDescription>
                            Add more documents or files to this request.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                          <FileUploadSection 
                            id="investor-additional-files-upload"
                            helpText="Upload any additional files relevant to this request." 
                            onFilesChange={(files) => handleUploadAdditionalFiles(files)}
                          />
                        </div>
                        <DialogFooter>
                          <p className="text-xs text-muted-foreground italic text-center w-full">
                            Files are uploaded immediately upon selection.
                          </p>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {Array.isArray(selectedRequest.documents) && selectedRequest.documents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedRequest.documents.map((doc, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleDownload(doc.fileUrl)}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors group text-left w-full"
                        >
                          <div className="flex items-center gap-3 overflow-hidden">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <Paperclip className="h-4 w-4 text-primary" />
                            </div>
                            <div className="overflow-hidden">
                              <p className="text-sm font-medium truncate" title={doc.documentName}>
                                {doc.documentName}
                              </p>
                              {doc.fileSizeBytes && (
                                <p className="text-[10px] text-muted-foreground">
                                  {(doc.fileSizeBytes / 1024).toFixed(1)} KB
                                </p>
                              )}
                            </div>
                          </div>
                          <Download className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic px-2">No attachments found.</p>
                  )}
                </div>

                {/* Timeline */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Requested</Label>
                    <p className="font-medium">{selectedRequest.requestedDate}</p>
                  </div>
                  {selectedRequest.dueDate && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Due Date</Label>
                      <p className="font-medium">{selectedRequest.dueDate}</p>
                    </div>
                  )}
                  {selectedRequest.completedDate && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Completed</Label>
                      <p className="font-medium text-green-600">
                        {selectedRequest.completedDate}
                      </p>
                    </div>
                  )}
                </div>

                {/* Research Content Section */}
                {selectedRequest.researchContent && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Research Content / Recommendation
                    </Label>
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/10 whitespace-pre-wrap text-sm italic">
                      {selectedRequest.researchContent}
                    </div>
                  </div>
                )}

                {/* Upcoming Meetings */}
                {upcomingMeetings.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      Upcoming Meetings
                    </Label>
                    <div className="space-y-2">
                      {upcomingMeetings.map((meeting) => (
                        <div key={meeting.id || meeting.meetingId} className="p-3 bg-blue-50/50 border border-blue-100 rounded-lg text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-blue-600" />
                              <div className="flex flex-col">
                                <span className="font-semibold text-blue-900">
                                  {(() => {
                                    const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+') 
                                      ? meeting.startTime 
                                      : `${meeting.startTime}Z`;
                                    return new Date(dateStr).toLocaleString([], { 
                                      month: 'short', 
                                      day: 'numeric', 
                                      hour: 'numeric', 
                                      minute: '2-digit'
                                    });
                                  })()}
                                </span>
                                <span className="text-[10px] text-blue-700/70">{meeting.meetingTitle || "Research Discussion"}</span>
                              </div>
                            </div>
                            <Badge 
                              variant="outline" 
                              className={cn(
                                "text-[10px] px-1.5 py-0 h-4.5 font-medium border-0",
                                meeting.status === "ACCEPTED" 
                                  ? "bg-green-100 text-green-700" 
                                  : "bg-yellow-100 text-yellow-700"
                              )}
                            >
                              {meeting.status}
                            </Badge>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex justify-end gap-2 pt-2 border-t border-blue-100/50">
                            {meeting.hostId === localStorage.getItem("userId") ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleUpdateMeetingStatus(meeting.id || meeting.meetingId, meeting.participantId, "CANCELLED")}
                              >
                                <XCircle className="mr-1 h-3 w-3" />
                                Cancel
                              </Button>
                            ) : meeting.status === "PENDING" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-[10px] text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleUpdateMeetingStatus(meeting.id || meeting.meetingId, localStorage.getItem("userId"), "ACCEPTED")}
                                >
                                  <CheckCircle2 className="mr-1 h-3 w-3" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleUpdateMeetingStatus(meeting.id || meeting.meetingId, localStorage.getItem("userId"), "DECLINED")}
                                >
                                  <XCircle className="mr-1 h-3 w-3" />
                                  Decline
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Schedule Meeting Button */}
                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => setIsScheduleDialogOpen(true)} 
                    className="w-full"
                    variant="outline"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    Schedule Meeting
                  </Button>
                </div>

                {/* Messages */}
                {selectedRequest.messages && selectedRequest.messages.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <Label className="mb-3">Messages</Label>
                      <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                        {selectedRequest.messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex gap-3 ${
                              message.sender === "investor" ? "flex-row-reverse" : ""
                            }`}
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="text-xs">
                                {message.senderName
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div
                              className={`flex-1 space-y-1 ${
                                message.sender === "investor" ? "items-end" : ""
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                  {message.senderName}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {message.timestamp}
                                </span>
                              </div>
                              <div
                                className={`p-3 rounded-lg text-sm ${
                                  message.sender === "investor"
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted"
                                }`}
                              >
                                {message.content}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          rows={2}
                        />
                        <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-20 text-center text-muted-foreground">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a request to view details</p>
                <p className="text-sm mb-4">
                  Choose from the list or create a new service request
                </p>
                <Button onClick={() => setIsRequestDialogOpen(true)}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  New Request
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ScheduleMeetingDialog 
        professional={selectedRequest ? { id: selectedRequest.provider.id, name: selectedRequest.provider.name } : null}
        requestId={selectedRequest?.id}
        isOpen={isScheduleDialogOpen}
        onOpenChange={setIsScheduleDialogOpen}
      />
    </div>
  );
}

function setIsLoading(arg0: boolean) {
  throw new Error("Function not implemented.");
}
